import React, { useState, useRef } from 'react';
import {
    Box,
    Button,
    Typography,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    LinearProgress,
    Alert,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    Delete as DeleteIcon,
    AttachFile as AttachFileIcon,
    Image as ImageIcon,
    PictureAsPdf as PdfIcon,
    Description as DocumentIcon,
    VideoFile as VideoIcon,
    AudioFile as AudioIcon,
    InsertDriveFile as FileIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { fileApi } from '../../../http';
import Viewer from 'react-viewer';
import OnlyOfficeEditor from '../../modules/DocumentServerNew';
import { useSelector } from 'react-redux';

// Функция для получения расширения файла
const getFileExtension = (filename) => {
    if (!filename) return 'txt';
    const ext = filename.split('.').pop().toLowerCase();
    return ext || 'txt';
};

const FileUploader = ({ 
    files = [], 
    onFilesChange, 
    maxFiles = 10, 
    maxFileSize = 50 * 1024 * 1024, // 50MB
    allowedTypes = ['image/*', 'application/pdf', 'text/*', 'video/*', 'audio/*'],
    darkMode = false,
    disabled = false,
    taskId = null
}) => {
    const theme = useTheme();
    const user = useSelector(state => state.state.user)
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});
    const [error, setError] = useState(null);
    const [previewDialog, setPreviewDialog] = useState({ open: false, file: null });
    const [viewerVisible, setViewerVisible] = useState(false);
    const [viewerImages, setViewerImages] = useState([]);
    const [activeImageIndex, setActiveImageIndex] = useState(0);
    const [videoPlayer, setVideoPlayer] = useState({ open: false, file: null });
    const [documentEditor, setDocumentEditor] = useState({ open: false, file: null });
    const [isDragOver, setIsDragOver] = useState(false);

    // Получить иконку для типа файла
    const getFileIcon = (fileType) => {
        if (fileType.startsWith('image/')) return <ImageIcon />;
        if (fileType === 'application/pdf') return <PdfIcon />;
        if (fileType.startsWith('video/')) return <VideoIcon />;
        if (fileType.startsWith('audio/')) return <AudioIcon />;
        if (fileType.startsWith('text/') || fileType.includes('document')) return <DocumentIcon />;
        return <FileIcon />;
    };

    // Форматирование размера файла
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Валидация файла
    const validateFile = (file) => {
        if (files.length >= maxFiles) {
            throw new Error(`Максимальное количество файлов: ${maxFiles}`);
        }

        if (file.size > maxFileSize) {
            throw new Error(`Размер файла превышает ${formatFileSize(maxFileSize)}`);
        }

        const isAllowedType = allowedTypes.some(type => {
            if (type.endsWith('/*')) {
                return file.type.startsWith(type.slice(0, -1));
            }
            return file.type === type;
        });

        if (!isAllowedType) {
            throw new Error(`Тип файла ${file.type} не поддерживается`);
        }

        return true;
    };

    // Загрузка файла в S3
    const uploadFile = async (file) => {
        try {
            validateFile(file);

            // Формируем key с ID задачи, если он передан
            const key = taskId ? `${taskId}/${file.name}` : `tasks/${file.name}`;
            const options = {
                bucket: 'tasks',
                key,
                type: file.type.startsWith('image/') ? 'image' : 
                      file.type.startsWith('video/') ? 'video' :
                      file.type.startsWith('audio/') ? 'audio' :
                      file.type.includes('pdf') ? 'document' : 'other',
                metadata: {
                    // originalName: file.name,
                    uploadedAt: new Date().toISOString(),
                    taskId: taskId ? String(taskId) : undefined
                },
                match_id: taskId,
                user_id: user.id
            };

            const result = await fileApi.uploadFile(file, options);
            
            if (result.success) {
                return result.data;
            } else {
                throw new Error(result.error || 'Ошибка загрузки файла');
            }
        } catch (error) {
            console.error('Ошибка загрузки файла:', error);
            throw error;
        }
    };

    // Обработка выбора файлов
    const handleFileSelect = async (event) => {
        const selectedFiles = Array.from(event.target.files);
        if (selectedFiles.length === 0) return;

        await processFiles(selectedFiles);
    };

    // Обработка перетаскивания файлов
    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!disabled) {
            setIsDragOver(true);
        }
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragOver(false);
    };

    const handleDrop = async (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragOver(false);

        if (disabled) return;

        const droppedFiles = Array.from(event.dataTransfer.files);
        if (droppedFiles.length === 0) return;

        await processFiles(droppedFiles);
    };

    // Общая функция обработки файлов
    const processFiles = async (selectedFiles) => {
        setError(null);
        setUploading(true);

        try {
            const uploadPromises = selectedFiles.map(async (file, index) => {
                try {
                    setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
                    
                    const uploadedFile = await uploadFile(file);
                    
                    setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
                    
                    return uploadedFile;
                } catch (error) {
                    setUploadProgress(prev => ({ ...prev, [file.name]: -1 }));
                    throw error;
                }
            });

            const uploadedFiles = await Promise.all(uploadPromises);
            const successfulFiles = uploadedFiles.filter(file => file !== null);
            
            if (successfulFiles.length > 0) {
                onFilesChange([...files, ...successfulFiles]);
            }

        } catch (error) {
            setError(error.message);
        } finally {
            setUploading(false);
            setUploadProgress({});
            // Очищаем input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    // Удаление файла
    const handleRemoveFile = async (fileToRemove) => {
        try {
            // Удаляем файл из S3
            await fileApi.deleteFile(fileToRemove.id);
            
            // Удаляем из списка
            const updatedFiles = files.filter(file => file.id !== fileToRemove.id);
            onFilesChange(updatedFiles);
        } catch (error) {
            console.error('Ошибка удаления файла:', error);
            setError('Ошибка удаления файла: ' + error.message);
        }
    };

    // Открытие превью
    const handlePreview = (file) => {
        if (file.type.startsWith('image/')) {
            // Для изображений используем react-viewer
            const imageFiles = files.filter(f => f.type.startsWith('image/'));
            const imageIndex = imageFiles.findIndex(f => f.id === file.id);
            setViewerImages(imageFiles.map(img => ({
                src: `/apig/s3/files/${img.id}/stream`,
                alt: img.name,
                downloadUrl: `/apig/s3/files/${img.id}/stream`
            })));
            setActiveImageIndex(imageIndex);
            setViewerVisible(true);
        } else if (file.type.startsWith('video/')) {
            // Для видео используем видео проигрыватель
            setVideoPlayer({ open: true, file });
        } else if (file.type === 'application/pdf' || 
                   file.type.includes('document') || 
                   file.type.includes('text') ||
                   /\.(doc|docx|xls|xlsx|ppt|pptx)$/i.test(file.name)) {
            // Для документов используем OnlyOffice
            setDocumentEditor({ open: true, file });
        } else {
            // Для других файлов используем обычный диалог
            setPreviewDialog({ open: true, file });
        }
    };

    // Закрытие превью
    const handleClosePreview = () => {
        setPreviewDialog({ open: false, file: null });
    };

    // Закрытие видео проигрывателя
    const handleCloseVideoPlayer = () => {
        setVideoPlayer({ open: false, file: null });
    };

    // Закрытие редактора документов
    const handleCloseDocumentEditor = () => {
        setDocumentEditor({ open: false, file: null });
    };

    return (
        <Box>
            {/* Кнопка загрузки */}
            <Paper
                elevation={1}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                sx={{
                    p: 2,
                    mb: 2,
                    border: `2px dashed ${isDragOver 
                        ? '#1976d2' 
                        : darkMode ? '#555' : '#ccc'}`,
                    borderRadius: 2,
                    textAlign: 'center',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.6 : 1,
                    bgcolor: isDragOver 
                        ? (darkMode ? 'rgba(25, 118, 210, 0.1)' : 'rgba(25, 118, 210, 0.05)')
                        : (darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'),
                    transition: 'all 0.3s ease',
                    '&:hover': disabled ? {} : {
                        borderColor: darkMode ? '#666' : '#999',
                        bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                    }
                }}
                onClick={() => !disabled && fileInputRef.current?.click()}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={allowedTypes.join(',')}
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                    disabled={disabled}
                />
                
                <UploadIcon 
                    sx={{ 
                        fontSize: 48, 
                        color: isDragOver 
                            ? '#1976d2' 
                            : darkMode ? '#666' : '#999',
                        mb: 1,
                        transform: isDragOver ? 'scale(1.1)' : 'scale(1)',
                        transition: 'all 0.3s ease'
                    }} 
                />
                
                <Typography 
                    variant="h6" 
                    sx={{ 
                        color: isDragOver 
                            ? '#1976d2' 
                            : darkMode ? '#ccc' : '#666',
                        mb: 1,
                        fontWeight: isDragOver ? 600 : 400
                    }}
                >
                    {uploading 
                        ? 'Загрузка файлов...' 
                        : isDragOver 
                            ? 'Отпустите файлы для загрузки' 
                            : 'Перетащите файлы сюда или нажмите для выбора'
                    }
                </Typography>
                
                <Typography 
                    variant="body2" 
                    sx={{ 
                        color: darkMode ? '#888' : '#999' 
                    }}
                >
                    Максимум {maxFiles} файлов, до {formatFileSize(maxFileSize)} каждый
                </Typography>
            </Paper>

            {/* Прогресс загрузки */}
            {uploading && Object.keys(uploadProgress).length > 0 && (
                <Box sx={{ mb: 2 }}>
                    {Object.entries(uploadProgress).map(([fileName, progress]) => (
                        <Box key={fileName} sx={{ mb: 1 }}>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                                {fileName} {progress === 100 ? '✓' : progress === -1 ? '✗' : `${progress}%`}
                            </Typography>
                            {progress >= 0 && progress < 100 && (
                                <LinearProgress 
                                    variant="determinate" 
                                    value={progress}
                                    sx={{ height: 4, borderRadius: 2 }}
                                />
                            )}
                        </Box>
                    ))}
                </Box>
            )}

            {/* Ошибка */}
            {error && (
                <Alert 
                    severity="error" 
                    sx={{ mb: 2 }}
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            {/* Список файлов */}
            {files.length > 0 && (
                <Paper 
                    elevation={1}
                    sx={{ 
                        bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                        borderRadius: 2
                    }}
                >
                    <List dense>
                        {files.map((file, index) => (
                            <ListItem key={file.id || index}>
                                <Box sx={{ mr: 2, color: darkMode ? '#ccc' : '#666' }}>
                                    {getFileIcon(file.type)}
                                </Box>
                                
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    fontWeight: 500,
                                                    color: darkMode ? '#e0e0e0' : '#333',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        textDecoration: 'underline'
                                                    }
                                                }}
                                                onClick={() => handlePreview(file)}
                                            >
                                                {file.name}
                                            </Typography>
                                            {file.hasPreview && (
                                                <Chip 
                                                    label="Превью" 
                                                    size="small" 
                                                    color="primary"
                                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                                />
                                            )}
                                        </Box>
                                    }
                                    secondary={
                                        <Typography 
                                            variant="caption" 
                                            sx={{ color: darkMode ? '#888' : '#666' }}
                                        >
                                            {formatFileSize(file.size)} • {file.type}
                                        </Typography>
                                    }
                                />
                                
                                <ListItemSecondaryAction>
                                    <IconButton
                                        edge="end"
                                        onClick={() => handleRemoveFile(file)}
                                        disabled={disabled}
                                        sx={{ 
                                            color: darkMode ? '#ff6b6b' : '#f44336',
                                            '&:hover': {
                                                bgcolor: darkMode ? 'rgba(255,107,107,0.1)' : 'rgba(244,67,54,0.1)'
                                            }
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            )}

            {/* Диалог превью */}
            <Dialog
                open={previewDialog.open}
                onClose={handleClosePreview}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    bgcolor: darkMode ? '#2d2d2d' : '#f5f7fa',
                    color: darkMode ? '#e0e0e0' : '#333'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AttachFileIcon />
                        <Typography variant="h6">
                            {previewDialog.file?.name}
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClosePreview}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                
                <DialogContent sx={{ 
                    p: 0,
                    bgcolor: darkMode ? '#1a1a1a' : '#fff'
                }}>
                    {previewDialog.file && (
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center',
                            minHeight: 400,
                            p: 2
                        }}>
                            {previewDialog.file.type.startsWith('image/') ? (
                                <img
                                    src={`/apig/s3/files/${previewDialog.file.id}/stream`}
                                    alt={previewDialog.file.name}
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '70vh',
                                        objectFit: 'contain'
                                    }}
                                />
                            ) : (
                                <Box sx={{ textAlign: 'center' }}>
                                    {getFileIcon(previewDialog.file.type)}
                                    <Typography variant="h6" sx={{ mt: 2, color: darkMode ? '#ccc' : '#666' }}>
                                        Превью недоступно
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: darkMode ? '#888' : '#999' }}>
                                        {previewDialog.file.type}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                
                <DialogActions sx={{ 
                    bgcolor: darkMode ? '#2d2d2d' : '#f5f7fa',
                    p: 2
                }}>
                    <Button 
                        onClick={handleClosePreview}
                        variant="outlined"
                    >
                        Закрыть
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* React Viewer для полноэкранного просмотра изображений */}
            {viewerVisible && (
                <Viewer
                    visible={viewerVisible}
                    onClose={() => setViewerVisible(false)}
                    images={viewerImages}
                    activeIndex={activeImageIndex}
                    zIndex={9999}
                />
            )}

            {/* Диалог видео проигрывателя */}
            <Dialog
                open={videoPlayer.open}
                onClose={handleCloseVideoPlayer}
                maxWidth="lg"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: darkMode ? '#2d2d2d' : '#f5f7fa',
                    color: darkMode ? '#e0e0e0' : '#333'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <VideoIcon />
                        <Typography variant="h6">
                            {videoPlayer.file?.name}
                        </Typography>
                    </Box>
                    <IconButton onClick={handleCloseVideoPlayer}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                
                <DialogContent sx={{ 
                    p: 0,
                    bgcolor: darkMode ? '#1a1a1a' : '#fff'
                }}>
                    {videoPlayer.file && (
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center',
                            minHeight: 400,
                            p: 2
                        }}>
                            <video
                                controls
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '70vh',
                                    width: '100%'
                                }}
                                src={`/apig/s3/files/${videoPlayer.file.id}/stream`}
                            >
                                Ваш браузер не поддерживает видео.
                            </video>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* Редактор документов OnlyOffice */}
            {documentEditor.open && documentEditor.file && (
                <OnlyOfficeEditor 
                    open={documentEditor.open}
                    onClose={handleCloseDocumentEditor}
                    doc={{
                        id: `${documentEditor.file.id}_${Date.now()}`,
                        name: documentEditor.file.name || 'document',
                        ext: getFileExtension(documentEditor.file.name || ''),
                        file: `/apig/s3/files/${documentEditor.file.id}/stream`
                    }}
                />
            )}
        </Box>
    );
};

export default FileUploader;
